import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { Network, Server, Database, Cloud, Shield, BarChart3 } from 'lucide-react';

const Architecture = () => {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLayer, setSelectedLayer] = useState('ALL');

  const fetchArchitecture = async () => {
    const data = await apiFetch(`${API}/architecture`);
    if (data?._error) {
      console.error('Error fetching architecture:', data.message);
      setLoading(false);
      return;
    }
    setComponents(data);
    setLoading(false);
  };

   
  useEffect(() => {
    fetchArchitecture();
  }, []);

  const layerIcons = {
    Ingestion: <Cloud className="text-blue-600" size={24} />,
    Storage: <Database className="text-green-600" size={24} />,
    Processing: <Server className="text-orange-600" size={24} />,
    Analytics: <BarChart3 className="text-purple-600" size={24} />,
    Governance: <Shield className="text-red-600" size={24} />
  };

  const layerColors = {
    Ingestion: { bg: 'bg-blue-50', border: 'border-blue-600', text: 'text-blue-800' },
    Storage: { bg: 'bg-green-50', border: 'border-green-600', text: 'text-green-800' },
    Processing: { bg: 'bg-orange-50', border: 'border-orange-600', text: 'text-orange-800' },
    Analytics: { bg: 'bg-purple-50', border: 'border-purple-600', text: 'text-purple-800' },
    Governance: { bg: 'bg-red-50', border: 'border-red-600', text: 'text-red-800' }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  const filteredComponents = selectedLayer === 'ALL' 
    ? components 
    : components.filter(c => c.layer === selectedLayer);

  const layers = ['Ingestion', 'Storage', 'Processing', 'Analytics', 'Governance'];

  return (
    <div className="space-y-6" data-testid="architecture-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">AWS Architecture</h1>
        <p className="text-slate-600">ESG Data Lakehouse & Double Materiality Engine</p>
      </div>

      {/* Architecture Diagram */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">High-Level Architecture</h2>
        <div className="font-mono text-xs bg-slate-900 text-green-400 p-6 rounded-lg overflow-x-auto">
          <pre>{`
                          ┌────────────────────────────────┐
                          │     Internal Data Sources       │
                          ├────────────────────────────────┤
                          │ HR | Procurement | Risk | CRM   │
                          │ Emissions Tools | Vendor ESG    │
                          └────────────────────────────────┘
                                        │
                                        ▼
                    ┌──────────────────────────────────────────┐
                    │         AWS Ingestion Layer               │
                    ├──────────────────────────────────────────┤
                    │   AWS Glue | Lambda | API Gateway         │
                    │   DMS (DB migrations)                     │
                    └──────────────────────────────────────────┘
                                        │
                                        ▼
                      ┌────────────────────────────────┐
                      │          ESG Data Lake         │
                      ├────────────────────────────────┤
                      │      S3 (Raw/Curated/Gold)     │
                      └────────────────────────────────┘
                                        │
                                        ▼
               ┌────────────────────────────────────────────────────┐
               │                ESG Data Warehouse                  │
               ├────────────────────────────────────────────────────┤
               │       Amazon Redshift / Athena SQL Layer           │
               └────────────────────────────────────────────────────┘
                                        │
                                        ▼
               ┌────────────────────────────────────────────────────┐
               │       Double Materiality Assessment Engine         │
               ├────────────────────────────────────────────────────┤
               │ Lambda | Step Functions | DynamoDB (Score Models) │
               │ Python Scoring Scripts | ML Forecast (optional)   │
               └────────────────────────────────────────────────────┘
                                        │
                                        ▼
                 ┌────────────────────────────────────────────────┐
                 │      ESG Reporting + Analytics Layer           │
                 ├────────────────────────────────────────────────┤
                 │ QuickSight Dashboards | API endpoints for BI   │
                 └────────────────────────────────────────────────┘
                                        │
                                        ▼
       ┌───────────────────────────────────────────────────────────────────┐
       │                   Governance, Security & Compliance                │
       ├───────────────────────────────────────────────────────────────────┤
       │ IAM | KMS | CloudTrail | Control Tower | Security Hub             │
       │ Data Quality Rules | Metadata via AWS Glue Data Catalog           │
       └───────────────────────────────────────────────────────────────────┘
          `}</pre>
        </div>
      </div>

      {/* Layer Filter */}
      <div className="bg-white rounded-lg shadow p-4 flex gap-2 items-center overflow-x-auto">
        <button
          onClick={() => setSelectedLayer('ALL')}
          className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
            selectedLayer === 'ALL' 
              ? 'bg-blue-600 text-white' 
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          All Layers
        </button>
        {layers.map(layer => (
          <button
            key={layer}
            onClick={() => setSelectedLayer(layer)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
              selectedLayer === layer 
                ? `${layerColors[layer].bg} ${layerColors[layer].text} border-2 ${layerColors[layer].border}` 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {layer}
          </button>
        ))}
      </div>

      {/* Components Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredComponents.map((component) => {
          const layerStyle = layerColors[component.layer];
          return (
            <div 
              key={component.id} 
              className={`bg-white rounded-lg shadow-lg border-l-4 ${layerStyle.border} p-6 hover:shadow-xl transition-shadow`}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className={`p-3 ${layerStyle.bg} rounded-lg`}>
                  {layerIcons[component.layer]}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{component.name}</h3>
                  <span className={`text-xs font-semibold px-2 py-1 ${layerStyle.bg} ${layerStyle.text} rounded`}>
                    {component.layer}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase block mb-1">AWS Service</span>
                  <p className="text-sm text-slate-700 font-medium">{component.aws_service}</p>
                </div>

                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase block mb-1">Purpose</span>
                  <p className="text-sm text-slate-700">{component.purpose}</p>
                </div>

                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase block mb-1">Description</span>
                  <p className="text-sm text-slate-600">{component.description}</p>
                </div>

                {component.connected_to && component.connected_to.length > 0 && (
                  <div>
                    <span className="text-xs font-semibold text-slate-500 uppercase block mb-2">Connected To</span>
                    <div className="flex flex-wrap gap-1">
                      {component.connected_to.map((conn, idx) => (
                        <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded">
                          {conn}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* AWS Well-Architected Note */}
      <div className="bg-gradient-to-r from-orange-50 to-orange-100 border-l-4 border-orange-600 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-orange-900 mb-2 flex items-center gap-2">
          <Shield size={24} />
          AWS Well-Architected Framework Alignment
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <strong className="text-orange-900">Security:</strong>
            <p className="text-orange-800">IAM, KMS encryption, CloudTrail audit logging</p>
          </div>
          <div>
            <strong className="text-orange-900">Reliability:</strong>
            <p className="text-orange-800">Multi-AZ deployments, S3 versioning, automated backups</p>
          </div>
          <div>
            <strong className="text-orange-900">Performance:</strong>
            <p className="text-orange-800">Athena for fast queries, Redshift for analytics</p>
          </div>
          <div>
            <strong className="text-orange-900">Cost Optimization:</strong>
            <p className="text-orange-800">S3 lifecycle policies, Spot instances, serverless architecture</p>
          </div>
          <div>
            <strong className="text-orange-900">Operational Excellence:</strong>
            <p className="text-orange-800">Glue automation, Step Functions orchestration</p>
          </div>
          <div>
            <strong className="text-orange-900">Sustainability:</strong>
            <p className="text-orange-800">Serverless-first design reduces carbon footprint</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Architecture;
